import { useActiveClients } from "../../../../../hooks/accessControl/useActiveClients";
import { useModuleEnabled } from "../../../../../hooks/gym/useModuleEnabled";
import { formatTime } from "../../../../../utils/format";
import { getClientDisplayName } from "../../../../../utils/client";
import type { ActiveClientEntry } from "../../../../../utils/zod/accessControl";

const SectionHeader = ({ count }: { count?: number }) => (
  <div className="flex items-center justify-between px-0.5">
    <div className="flex items-center gap-1.5">
      <svg
        className="w-3.5 h-3.5 text-global-accent"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
        />
      </svg>
      <h3 className="text-size-2xs font-semibold text-global-secondary uppercase tracking-wide">
        Accesos recientes
      </h3>
    </div>
    {count !== undefined && (
      <span className="px-1.5 py-0.5 bg-global-accent/20 text-global-accent rounded text-size-2xs font-bold">
        {count}
      </span>
    )}
  </div>
);

const AccessItem = ({ entry }: { entry: ActiveClientEntry }) => (
  <div className="flex items-center gap-2 bg-global-overlay-secondary rounded-md p-2 border border-global-secondary">
    <span
      className="w-2 h-2 rounded-full shrink-0"
      style={{
        backgroundColor: entry.isException
          ? "var(--color-warning-text)"
          : "var(--color-success-text)",
      }}
    />
    <div className="min-w-0 flex-1">
      <p className="text-size-2xs font-medium text-global-primary truncate">
        {entry.isGuest ? entry.firstName : getClientDisplayName(entry)}
      </p>
      {entry.isException && entry.exceptionReason && (
        <p
          className="text-size-2xs text-global-tertiary truncate"
          title={entry.exceptionReason}
        >
          {entry.exceptionReason}
        </p>
      )}
    </div>
    <span className="text-size-2xs text-global-muted shrink-0">
      {formatTime(entry.enteredAt)}
    </span>
  </div>
);

export const RecentAccessNotifications = () => {
  const accessControlEnabled = useModuleEnabled("accessControl");
  const { clients, total, isLoading } = useActiveClients(1, {
    enabled: accessControlEnabled,
  });

  if (!accessControlEnabled) return null;

  if (isLoading) {
    return (
      <div className="space-y-2">
        <SectionHeader />
        <div className="bg-global-overlay-secondary rounded-md p-3 border border-global-secondary text-center">
          <p className="text-size-2xs text-global-muted">Cargando...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <SectionHeader count={total} />

      {clients.length === 0 ? (
        <div className="bg-global-overlay-secondary rounded-md p-3 border border-global-secondary text-center">
          <p className="text-size-2xs text-global-muted">No hubo ingresos hoy</p>
        </div>
      ) : (
        <div className="space-y-1 max-h-48 overflow-y-auto custom-scrollbar">
          {clients.map((entry) => (
            <AccessItem
              key={`${entry.publicId}-${entry.enteredAt}`}
              entry={entry}
            />
          ))}
        </div>
      )}
    </div>
  );
};
